import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Loader2, Upload, Trash2, Plus, GripVertical, Film, Images, Package,
  Award, MapPin, ShoppingBag, Save, X, Link2, Sparkles, Check, Maximize2,
} from 'lucide-react';
import {
  getArtwork,
  updateArtwork,
  saveArtworkSections,
  listArtworkImages,
  addArtworkImage,
  updateArtworkImage,
  deleteArtworkImage,
  reorderArtworkImages,
  listArtworkProvenance,
  addArtworkProvenance,
  updateArtworkProvenance,
  deleteArtworkProvenance,
  requestArtworkUploadUrl,
  directUploadToSupabase,
  aiGenerateImage,
  aiGenerateCopy,
  aiApproveGeneration,
  aiDiscardGeneration,
} from '../lib/artworksApi';

const inputCls =
  'w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40 transition-colors';
const textareaCls = `${inputCls} resize-y`;

const GLAZING_OPTIONS = ['None (unglazed)', 'Museum glass (anti-reflective, UV)', 'Anti-reflective acrylic / plexi (UV)', 'Standard glass', 'Acrylic / plexiglass', 'UV-protective glazing'];
const PAPER_OPTIONS = ['Hahnemühle Photo Rag 308gsm', 'Hahnemühle German Etching 310gsm', 'Canson Infinity Rag Photographique', 'Fine art cotton rag', 'Archival matte paper', 'Giclée canvas'];
const FRAME_MATERIAL_OPTIONS = ['Aluminium', 'Oak', 'Ash', 'Walnut', 'Maple', 'Black wood', 'White wood'];
const FRAME_COLOUR_OPTIONS = ['Natural oak', 'Black', 'Matte black', 'White', 'Walnut', 'Natural wood', 'Silver'];

const SECTION_ORDER = { film: 1, look_closer: 2, object: 3, edition: 4, in_context: 5, provenance: 6, acquisition: 7 };

const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const toStr = (v) => (v && String(v).trim() ? String(v).trim() : null);

async function uploadToStorage(file) {
  const signed = await requestArtworkUploadUrl({ file_name: file.name, mime_type: file.type, kind: 'media' });
  await directUploadToSupabase(signed.signed_url, file);
  return { url: signed.public_url, path: signed.path, bucket: signed.bucket_name };
}

// ─── Small atoms ────────────────────────────────────────────────

function Toggle({ enabled, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={enabled}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${enabled ? 'bg-[#90E0EF]' : 'bg-[#1a1a1a]'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-[#050505] transition-transform ${enabled ? 'translate-x-5' : ''}`} />
    </button>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-2">{children}</label>;
}

function CheckRow({ label, checked, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center justify-between px-3 py-2.5 border text-left transition-colors ${checked ? 'border-[#90E0EF]/40 bg-[#90E0EF]/5 text-[#90E0EF]' : 'border-[#1a1a1a] text-[#888] hover:border-[#333]'}`}
    >
      <span className="text-[12px] font-mono uppercase tracking-[0.12em]">{label}</span>
      <span className={`w-4 h-4 border flex items-center justify-center ${checked ? 'border-[#90E0EF] bg-[#90E0EF] text-[#050505]' : 'border-[#333]'}`}>
        {checked && <span className="text-[10px] leading-none">✓</span>}
      </span>
    </button>
  );
}

function SelectWithOther({ value, onChange, options, placeholder, testid }) {
  const [other, setOther] = useState(() => !!value && !options.includes(value));
  useEffect(() => { if (value && !options.includes(value)) setOther(true); }, [value, options]);
  const selectVal = other ? '__other__' : (options.includes(value) ? value : '');
  return (
    <div className="space-y-2">
      <select value={selectVal}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__other__') { setOther(true); onChange(''); }
          else { setOther(false); onChange(v); }
        }}
        className={`${inputCls} cursor-pointer`} data-testid={testid}>
        <option value="">{placeholder || 'Select…'}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
        <option value="__other__">Other…</option>
      </select>
      {other && (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Enter custom value"
          className={inputCls} data-testid={`${testid}-other`} />
      )}
    </div>
  );
}

function StoryCard({ icon: Icon, step, title, hint, hasToggle = true, enabled, onToggle, children, testid }) {
  return (
    <section data-testid={testid} className="border border-[#1a1a1a] bg-[#0a0a0a]">
      <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[#141414]">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-8 h-8 border border-[#1a1a1a] flex items-center justify-center text-[#90E0EF] shrink-0">
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-[#90E0EF] tracking-[0.2em]">{step}</span>
              <h3 className="text-[15px] font-outfit font-semibold text-white truncate">{title}</h3>
            </div>
            {hint && <p className="text-[11px] font-mono text-[#666] mt-0.5 truncate">{hint}</p>}
          </div>
        </div>
        {hasToggle && (
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-[9px] font-mono uppercase tracking-[0.18em] ${enabled ? 'text-[#90E0EF]' : 'text-[#555]'}`}>
              {enabled ? 'Shown' : 'Hidden'}
            </span>
            <Toggle enabled={enabled} onClick={onToggle} testid={`${testid}-toggle`} />
          </div>
        )}
      </header>
      <div className={`px-6 py-5 space-y-4 transition-opacity ${hasToggle && !enabled ? 'opacity-50' : ''}`}>{children}</div>
    </section>
  );
}

// ─── Single-asset upload (film mp4, poster, certificate) ────────

function AssetUpload({ value, onChange, accept, kind, testid, previewType = 'image' }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    const maxMb = previewType === 'video' ? 300 : 30;
    if (file.size > maxMb * 1024 * 1024) { toast.error(`File too large (max ${maxMb}MB)`); return; }
    setBusy(true);
    try {
      const { url } = await uploadToStorage(file);
      onChange(url);
      toast.success('Uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="w-28 h-20 border border-[#1a1a1a] bg-[#050505] overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            previewType === 'video'
              ? <video src={value} className="w-full h-full object-cover" muted />
              : <img src={value} alt="" className="w-full h-full object-contain" />
          ) : (
            <span className="text-[9px] font-mono text-[#444] uppercase">None</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={ref} type="file" accept={accept} className="hidden" data-testid={`${testid}-file`}
            onChange={(e) => pick(e.target.files?.[0])} />
          <div className="flex gap-2">
            <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload`}
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#1a1a1a] hover:border-[#90E0EF]/40 text-[12px] font-mono text-[#c8c8c8] transition-colors disabled:opacity-40">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {busy ? 'Uploading…' : 'Upload'}
            </button>
            {value && (
              <button type="button" onClick={() => onChange('')} data-testid={`${testid}-clear`}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#1a1a1a] hover:border-[#e28f8f] text-[12px] font-mono text-[#e28f8f] transition-colors">
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link2 className="w-3.5 h-3.5 text-[#555] shrink-0" />
            <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="…or paste a hosted URL"
              className={inputCls} data-testid={`${testid}-url`} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Multi-image manager for a story section ────────────────────

function SectionMediaManager({ artworkId, section, single = false, label, reloadSignal }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef(null);
  const dragging = useRef(null);

  const refresh = () => {
    setLoading(true);
    listArtworkImages(artworkId, section)
      .then((d) => setItems(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [artworkId, section, reloadSignal]);

  const doUpload = async (files) => {
    if (!files || files.length === 0) return;
    const list = single ? files.slice(0, 1) : files;
    setUploading(true);
    try {
      for (const file of list) {
        if (file.size > 30 * 1024 * 1024) { toast.error(`${file.name}: too large (max 30MB)`); continue; }
        const { url, path, bucket } = await uploadToStorage(file);
        await addArtworkImage(artworkId, {
          image_url: url, storage_path: path, bucket_name: bucket,
          alt_text: file.name.replace(/\.[^.]+$/, ''), section,
        });
      }
      toast.success('Uploaded');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveCaption = async (id, caption) => {
    try { await updateArtworkImage(artworkId, id, { caption }); } catch (_e) { /* ignore */ }
  };
  const remove = async (id) => {
    setBusyId(id);
    try { await deleteArtworkImage(artworkId, id); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    finally { setBusyId(null); }
  };

  const onDrop = (targetId) => async (e) => {
    e.preventDefault();
    const dragId = dragging.current; dragging.current = null;
    if (!dragId || dragId === targetId) return;
    const ordered = [...items];
    const from = ordered.findIndex((x) => x.id === dragId);
    const to = ordered.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return;
    const [m] = ordered.splice(from, 1);
    ordered.splice(to, 0, m);
    setItems(ordered);
    try { await reorderArtworkImages(artworkId, ordered.map((x) => x.id)); }
    catch (_e) { refresh(); }
  };

  return (
    <div data-testid={`media-${section}`}>
      <div className="flex items-center gap-3 mb-3">
        <input ref={fileRef} type="file" accept="image/*" multiple={!single} className="hidden"
          data-testid={`media-${section}-input`} onChange={(e) => doUpload(Array.from(e.target.files || []))} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || (single && items.length >= 1)}
          data-testid={`media-${section}-add`}
          className="inline-flex items-center gap-2 px-3 py-2 border border-[#1a1a1a] hover:border-[#90E0EF]/40 text-[12px] font-mono text-[#c8c8c8] transition-colors disabled:opacity-40">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {single ? 'Upload image' : 'Add images'}
        </button>
        {items.length > 0 && <span className="text-[10px] font-mono text-[#666] uppercase tracking-[0.14em]">{items.length} item{items.length === 1 ? '' : 's'}</span>}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-[11px] font-mono text-[#666]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="text-[11px] font-mono text-[#555] border border-dashed border-[#1a1a1a] px-3 py-4">{label || 'No media yet.'}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AnimatePresence initial={false}>
            {items.map((im) => (
              <motion.div key={im.id} layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
                draggable={!single}
                onDragStart={(e) => { dragging.current = im.id; e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={onDrop(im.id)}
                data-testid={`media-${section}-tile-${im.id}`}
                className="border border-[#1a1a1a] bg-[#050505] overflow-hidden">
                <div className="relative aspect-video">
                  <img src={im.image_url} alt="" className="w-full h-full object-cover" />
                  {!single && <GripVertical className="absolute top-2 left-2 w-4 h-4 text-white/50" />}
                  <button type="button" onClick={() => remove(im.id)} disabled={busyId === im.id}
                    data-testid={`media-${section}-del-${im.id}`}
                    className="absolute top-2 right-2 p-1.5 border border-[#1a1a1a] bg-[#050505]/90 hover:border-[#e28f8f] text-[#e28f8f] transition-colors disabled:opacity-40">
                    {busyId === im.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </button>
                </div>
                <input defaultValue={im.caption || ''} placeholder="Caption (optional)"
                  onBlur={(e) => saveCaption(im.id, e.target.value)}
                  data-testid={`media-${section}-caption-${im.id}`}
                  className="w-full bg-[#0a0a0a] border-t border-[#1a1a1a] px-3 py-2 text-[12px] text-[#e8e8e8] placeholder:text-[#444] focus:outline-none" />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

// ─── Provenance editor ──────────────────────────────────────────

const PROV_FIELDS = [
  { k: 'event_name', ph: 'Exhibition / event' },
  { k: 'institution', ph: 'Institution / gallery' },
  { k: 'city', ph: 'City' },
  { k: 'country', ph: 'Country' },
  { k: 'year', ph: 'Year' },
  { k: 'url', ph: 'Link (optional)' },
];

function ProvenanceRow({ record, onSave, onDelete }) {
  const [draft, setDraft] = useState(record);
  const [busy, setBusy] = useState(false);
  const isNew = !record.id;
  const save = async () => {
    setBusy(true);
    try { await onSave(draft); } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#1a1a1a] bg-[#050505] p-3 space-y-2" data-testid={`prov-row-${record.id || 'new'}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {PROV_FIELDS.map((f) => (
          <input key={f.k} value={draft[f.k] || ''} placeholder={f.ph}
            onChange={(e) => setDraft((d) => ({ ...d, [f.k]: e.target.value }))}
            data-testid={`prov-${f.k}-${record.id || 'new'}`}
            className="bg-[#0a0a0a] border border-[#1a1a1a] px-3 py-2 text-[12px] text-[#f0f0f0] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40" />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={save} disabled={busy} data-testid={`prov-save-${record.id || 'new'}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#90E0EF]/40 text-[11px] font-mono uppercase tracking-[0.14em] text-[#90E0EF] hover:bg-[#90E0EF]/5 transition-colors disabled:opacity-40">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}{isNew ? 'Add record' : 'Save'}
        </button>
        {!isNew && (
          <button type="button" onClick={() => onDelete(record.id)} data-testid={`prov-del-${record.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#1a1a1a] hover:border-[#e28f8f] text-[11px] font-mono uppercase tracking-[0.14em] text-[#e28f8f] transition-colors">
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

function ProvenanceEditor({ artworkId }) {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const refresh = () => { listArtworkProvenance(artworkId).then((d) => setRows(d || [])).catch(() => {}); };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [artworkId]);

  const saveExisting = async (draft) => {
    try {
      await updateArtworkProvenance(artworkId, draft.id, {
        event_name: toStr(draft.event_name), institution: toStr(draft.institution),
        city: toStr(draft.city), country: toStr(draft.country), year: toStr(draft.year), url: toStr(draft.url),
      });
      toast.success('Record saved');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const saveDraft = (key) => async (draft) => {
    if (!toStr(draft.event_name) && !toStr(draft.institution)) { toast.error('Add an event or institution'); return; }
    try {
      await addArtworkProvenance(artworkId, {
        event_name: toStr(draft.event_name), institution: toStr(draft.institution),
        city: toStr(draft.city), country: toStr(draft.country), year: toStr(draft.year), url: toStr(draft.url),
        display_order: rows.length,
      });
      toast.success('Record added');
      setDrafts((ds) => ds.filter((d) => d.__key !== key));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };
  const del = async (id) => {
    try { await deleteArtworkProvenance(artworkId, id); refresh(); } catch (_e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-3" data-testid="provenance-editor">
      {rows.map((r) => (
        <ProvenanceRow key={r.id} record={r} onSave={saveExisting} onDelete={del} />
      ))}
      {drafts.map((d) => (
        <ProvenanceRow key={d.__key} record={d} onSave={saveDraft(d.__key)} onDelete={() => setDrafts((ds) => ds.filter((x) => x.__key !== d.__key))} />
      ))}
      <button type="button" onClick={() => setDrafts((ds) => [...ds, { __key: Date.now() }])}
        data-testid="prov-add-btn"
        className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-[#1a1a1a] hover:border-[#90E0EF]/40 text-[12px] font-mono text-[#888] hover:text-[#90E0EF] transition-colors">
        <Plus className="w-3.5 h-3.5" /> Add provenance record
      </button>
    </div>
  );
}

// ─── AI assistants ──────────────────────────────────────────────

export function CopyAssist({ artworkId, target, onApply }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const d = await aiGenerateCopy(artworkId, { target, custom_prompt: prompt || undefined });
      setResult({ id: d.id, text: d.text || d.output_text || '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI copy failed');
    } finally { setBusy(false); }
  };
  const discard = async () => {
    if (result?.id) { try { await aiDiscardGeneration(artworkId, result.id); } catch (_e) { /* ignore */ } }
    setResult(null);
  };
  const close = () => { setOpen(false); discard(); setPrompt(''); };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={`copy-assist-${target}-open`}
        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono text-[#90E0EF] hover:text-white transition-colors">
        <Sparkles className="w-3.5 h-3.5" /> Draft with AI
      </button>
    );
  }
  return (
    <div className="mt-2 border border-[#1a1a1a] bg-[#050505] p-3 space-y-2" data-testid={`copy-assist-${target}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#90E0EF] flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI copy</span>
        <button type="button" onClick={close} className="text-[#666] hover:text-white"><X className="w-3.5 h-3.5" /></button>
      </div>
      <input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Optional: steer the tone or focus…"
        className={inputCls} data-testid={`copy-assist-${target}-prompt`} />
      {result && (
        <p className="text-[13px] leading-relaxed text-[#d8d8d8] border-l-2 border-[#90E0EF]/40 pl-3 whitespace-pre-wrap" data-testid={`copy-assist-${target}-result`}>{result.text}</p>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={run} disabled={busy} data-testid={`copy-assist-${target}-run`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#90E0EF]/40 text-[11px] font-mono uppercase tracking-[0.12em] text-[#90E0EF] hover:bg-[#90E0EF]/5 transition-colors disabled:opacity-40">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{result ? 'Regenerate' : 'Generate'}
        </button>
        {result && (
          <button type="button" onClick={() => { onApply(result.text); toast.success('Applied — remember to Save story'); setOpen(false); setResult(null); setPrompt(''); }}
            data-testid={`copy-assist-${target}-use`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#90E0EF] text-[#050505] text-[11px] font-mono uppercase tracking-[0.12em] hover:bg-[#b3ebf5] transition-colors">
            <Check className="w-3 h-3" /> Use this
          </button>
        )}
      </div>
    </div>
  );
}

const PRESENTATION_OPTIONS = [
  { v: 'auto', label: 'Auto — detect from medium' },
  { v: 'framed_work', label: 'Framed work (print / photo / painting)' },
  { v: 'sculpture', label: 'Sculpture / object (on a plinth)' },
  { v: 'digital_screen', label: 'Digital (shown on a screen / TV)' },
];

function ImageAssist({ artworkId, kind, title, onApproved, defaultPresentation }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [presentation, setPresentation] = useState(defaultPresentation || 'auto');
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(false);
  const [result, setResult] = useState(null);
  const [full, setFull] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const d = await aiGenerateImage(artworkId, {
        kind,
        custom_prompt: prompt || undefined,
        presentation: presentation !== 'auto' ? presentation : undefined,
      });
      setResult({ id: d.id, url: d.output_url });
    } catch (e) {
      const msg = e?.response?.status === 404
        ? 'Artwork not found — reload the page (it may have been deleted) and try again.'
        : (e?.response?.data?.detail || 'AI image failed');
      toast.error(msg);
    } finally { setBusy(false); }
  };
  const discard = async () => {
    if (result?.id) { try { await aiDiscardGeneration(artworkId, result.id); } catch (_e) { /* ignore */ } }
    setResult(null);
  };
  const close = () => { setOpen(false); discard(); setPrompt(''); setFull(false); };
  const approve = async () => {
    if (!result?.id) return;
    setApproving(true);
    try {
      await aiApproveGeneration(artworkId, result.id);
      toast.success('Added to this section');
      setResult(null); setOpen(false); setPrompt(''); setFull(false);
      onApproved && onApproved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setApproving(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={`image-assist-${kind}-open`}
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#90E0EF]/40 text-[12px] font-mono text-[#90E0EF] hover:bg-[#90E0EF]/5 transition-colors">
        <Sparkles className="w-3.5 h-3.5" /> {title || 'Generate with AI'}
      </button>
    );
  }
  return (
    <>
      <div className="border border-[#1a1a1a] bg-[#050505] p-3 space-y-2" data-testid={`image-assist-${kind}`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#90E0EF] flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> {title || 'AI image'}</span>
          <button type="button" onClick={close} className="text-[#666] hover:text-white"><X className="w-3.5 h-3.5" /></button>
        </div>
        <p className="text-[10px] font-mono text-[#666] leading-relaxed">Uses the original artwork as a locked reference — never redrawn, and no reflections are added. Save the story first so AI uses your latest details. This can take up to a minute.</p>
        <div>
          <FieldLabel>How should this work be shown?</FieldLabel>
          <select value={presentation} onChange={(e) => setPresentation(e.target.value)}
            className={`${inputCls} cursor-pointer`} data-testid={`image-assist-${kind}-presentation`}>
            {PRESENTATION_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
        <input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Optional: describe the scene / setting you want…"
          className={inputCls} data-testid={`image-assist-${kind}-prompt`} />
        {result && (
          <div className="relative group">
            <div className="aspect-video w-full overflow-hidden border border-[#1a1a1a] bg-black cursor-zoom-in"
              data-testid={`image-assist-${kind}-preview`} onClick={() => setFull(true)}>
              <img src={result.url} alt="AI preview" className="w-full h-full object-contain" />
            </div>
            <button type="button" onClick={() => setFull(true)} data-testid={`image-assist-${kind}-fullscreen-btn`}
              className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#050505]/80 border border-[#1f1f1f] text-[10px] font-mono uppercase tracking-[0.12em] text-white opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="w-3 h-3" /> Full screen
            </button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={run} disabled={busy || approving} data-testid={`image-assist-${kind}-run`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#90E0EF]/40 text-[11px] font-mono uppercase tracking-[0.12em] text-[#90E0EF] hover:bg-[#90E0EF]/5 transition-colors disabled:opacity-40">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{busy ? 'Generating…' : (result ? 'Regenerate' : 'Generate')}
          </button>
          {result && (
            <button type="button" onClick={approve} disabled={approving} data-testid={`image-assist-${kind}-approve`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#90E0EF] text-[#050505] text-[11px] font-mono uppercase tracking-[0.12em] hover:bg-[#b3ebf5] transition-colors disabled:opacity-40">
              {approving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve &amp; add
            </button>
          )}
        </div>
      </div>
      {full && result && (
        <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-6" onClick={() => setFull(false)} data-testid={`image-assist-${kind}-fullscreen`}>
          <img src={result.url} alt="AI preview full" className="max-w-full max-h-full object-contain" />
          <button type="button" onClick={() => setFull(false)}
            className="absolute top-5 right-5 p-2 border border-[#1f1f1f] bg-[#050505]/80 text-white hover:border-white/40 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
}

// ─── Main editor ────────────────────────────────────────────────

const DEFAULT_SECTIONS = {
  film: { enabled: false, heading: '', body: '' },
  look_closer: { enabled: false, heading: '', body: '' },
  object: { enabled: false, heading: '', body: '' },
  edition: { enabled: false, heading: '', body: '' },
  in_context: { enabled: false, heading: '', body: '' },
  provenance: { enabled: false, heading: '', body: '' },
  acquisition: { enabled: true, heading: '', body: '' },
};

export default function ArtworkStoryEditor({ artworkId, artistName, onReload }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState({});
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [mediaReload, setMediaReload] = useState({ object: 0, in_context: 0 });
  const bumpMedia = (k) => setMediaReload((m) => ({ ...m, [k]: (m[k] || 0) + 1 }));

  const load = () => {
    setLoading(true);
    getArtwork(artworkId).then((row) => {
      setFields({
        width_cm: row.width_cm ?? '', height_cm: row.height_cm ?? '', depth_cm: row.depth_cm ?? '',
        paper: row.paper || '', frame_material: row.frame_material || '', frame_colour: row.frame_colour || '', glazing: row.glazing || '',
        edition_number: row.edition_number ?? '', edition_total: row.edition_total ?? '',
        signed: !!row.signed, numbered: !!row.numbered, certificate_included: !!row.certificate_included,
        certificate_image_url: row.certificate_image_url || '', certification_text: row.certification_text || '',
        film_url: row.film_url || '', film_poster_url: row.film_poster_url || '',
        presentation_type: row.presentation_type || '',
      });
      const map = { ...DEFAULT_SECTIONS };
      (row.sections || []).forEach((s) => {
        map[s.section_type] = { enabled: !!s.enabled, heading: s.heading || '', body: s.body || '' };
      });
      setSections(map);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [artworkId]);

  const setF = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const setSec = (type, patch) => setSections((s) => ({ ...s, [type]: { ...s[type], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await updateArtwork(artworkId, {
        width_cm: toNum(fields.width_cm), height_cm: toNum(fields.height_cm), depth_cm: toNum(fields.depth_cm),
        paper: toStr(fields.paper), frame_material: toStr(fields.frame_material), frame_colour: toStr(fields.frame_colour), glazing: toStr(fields.glazing),
        edition_number: toNum(fields.edition_number),
        signed: !!fields.signed, numbered: !!fields.numbered, certificate_included: !!fields.certificate_included,
        certificate_image_url: toStr(fields.certificate_image_url), certification_text: toStr(fields.certification_text),
        film_url: toStr(fields.film_url), film_poster_url: toStr(fields.film_poster_url),
        presentation_type: fields.presentation_type || null,
      });
      await saveArtworkSections(artworkId, Object.entries(sections).map(([type, v]) => ({
        section_type: type, enabled: !!v.enabled, heading: toStr(v.heading), body: toStr(v.body),
        display_order: SECTION_ORDER[type] ?? 0,
      })));
      toast.success('Story page saved');
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const headBodyFields = (type, headingPh, bodyPh, copyTarget) => (
    <div className="grid grid-cols-1 gap-3">
      <div>
        <FieldLabel>Section heading</FieldLabel>
        <input value={sections[type].heading} onChange={(e) => setSec(type, { heading: e.target.value })}
          placeholder={headingPh} className={inputCls} data-testid={`story-${type}-heading`} />
      </div>
      <div>
        <FieldLabel>Section text</FieldLabel>
        <textarea rows={3} value={sections[type].body} onChange={(e) => setSec(type, { body: e.target.value })}
          placeholder={bodyPh} className={textareaCls} data-testid={`story-${type}-body`} />
        {copyTarget && <CopyAssist artworkId={artworkId} target={copyTarget} onApply={(t) => setSec(type, { body: t })} />}
      </div>
    </div>
  );

  if (loading) {
    return (
      <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-10 flex items-center justify-center" data-testid="story-editor-loading">
        <Loader2 className="w-5 h-5 text-[#90E0EF] animate-spin" />
      </section>
    );
  }

  return (
    <div className="space-y-5" data-testid="artwork-story-editor">
      {/* Header + save */}
      <div className="flex items-center justify-between gap-4 border border-[#1a1a1a] bg-[#0a0a0a] px-6 py-4 sticky top-24 z-10">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#555]">Story page</p>
          <h2 className="text-lg font-outfit font-semibold text-white">Modular artwork experience</h2>
          <p className="text-[11px] font-mono text-[#666] mt-0.5">Each section is hidden on the public page unless it is turned on and has content.</p>
        </div>
        <button type="button" onClick={save} disabled={saving} data-testid="story-save-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#90E0EF] text-[#050505] text-[12px] font-mono uppercase tracking-[0.14em] hover:bg-[#b3ebf5] transition-colors disabled:opacity-50 shrink-0">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save story'}
        </button>
      </div>

      {/* FILM */}
      <StoryCard icon={Film} step="02" title="Film" hint="A cinematic 16:9 video of the work."
        enabled={sections.film.enabled} onToggle={() => setSec('film', { enabled: !sections.film.enabled })} testid="story-card-film">
        {headBodyFields('film', 'Film', 'A short line that introduces the film.', 'film')}
        <div>
          <FieldLabel>Film video (MP4 upload or hosted URL)</FieldLabel>
          <AssetUpload value={fields.film_url} onChange={(v) => setF('film_url', v)} accept="video/mp4,video/*" previewType="video" testid="story-film-video" />
        </div>
        <div>
          <FieldLabel>Poster image (shown before play)</FieldLabel>
          <AssetUpload value={fields.film_poster_url} onChange={(v) => setF('film_poster_url', v)} accept="image/*" testid="story-film-poster" />
        </div>
      </StoryCard>

      {/* LOOK CLOSER */}
      <StoryCard icon={Images} step="03" title="Look closer" hint="Detail shots the visitor can step through."
        enabled={sections.look_closer.enabled} onToggle={() => setSec('look_closer', { enabled: !sections.look_closer.enabled })} testid="story-card-look_closer">
        {headBodyFields('look_closer', 'Look closer', 'What should the viewer notice in these details?', 'look_closer')}
        <div>
          <FieldLabel>Detail media (drag to reorder, add captions)</FieldLabel>
          <SectionMediaManager artworkId={artworkId} section="look_closer" label="Add close-up detail images." />
        </div>
      </StoryCard>

      {/* THE OBJECT */}
      <StoryCard icon={Package} step="04" title="The object" hint="Materials, craft and physical dimensions."
        enabled={sections.object.enabled} onToggle={() => setSec('object', { enabled: !sections.object.enabled })} testid="story-card-object">
        {headBodyFields('object', 'Crafted for a longer tomorrow', 'Describe the craft and material choices.', 'object')}
        <div>
          <FieldLabel>Presentation type (how AI renders this work)</FieldLabel>
          <select value={fields.presentation_type || 'auto'}
            onChange={(e) => setF('presentation_type', e.target.value === 'auto' ? '' : e.target.value)}
            className={`${inputCls} cursor-pointer`} data-testid="story-presentation-type">
            {PRESENTATION_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <p className="mt-1.5 text-[10px] font-mono text-[#555]">Saved with the work and used as the default for AI renders. "Auto" detects it from the medium.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><FieldLabel>Paper</FieldLabel><SelectWithOther value={fields.paper} onChange={(v) => setF('paper', v)} options={PAPER_OPTIONS} placeholder="Select paper…" testid="story-paper" /></div>
          <div><FieldLabel>Glazing</FieldLabel><SelectWithOther value={fields.glazing} onChange={(v) => setF('glazing', v)} options={GLAZING_OPTIONS} placeholder="Select glazing…" testid="story-glazing" /></div>
          <div><FieldLabel>Frame material</FieldLabel><SelectWithOther value={fields.frame_material} onChange={(v) => setF('frame_material', v)} options={FRAME_MATERIAL_OPTIONS} placeholder="Select frame material…" testid="story-frame-material" /></div>
          <div><FieldLabel>Frame colour</FieldLabel><SelectWithOther value={fields.frame_colour} onChange={(v) => setF('frame_colour', v)} options={FRAME_COLOUR_OPTIONS} placeholder="Select frame colour…" testid="story-frame-colour" /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><FieldLabel>Width (cm)</FieldLabel><input type="number" value={fields.width_cm} onChange={(e) => setF('width_cm', e.target.value)} placeholder="20" className={inputCls} data-testid="story-width" /></div>
          <div><FieldLabel>Height (cm)</FieldLabel><input type="number" value={fields.height_cm} onChange={(e) => setF('height_cm', e.target.value)} placeholder="30" className={inputCls} data-testid="story-height" /></div>
          <div><FieldLabel>Depth (cm)</FieldLabel><input type="number" value={fields.depth_cm} onChange={(e) => setF('depth_cm', e.target.value)} placeholder="2" className={inputCls} data-testid="story-depth" /></div>
        </div>
        <div>
          <FieldLabel>Frame / detail image (optional)</FieldLabel>
          <SectionMediaManager artworkId={artworkId} section="object" label="Upload a frame or material detail image, or generate one below." reloadSignal={mediaReload.object} />
          <div className="mt-3">
            <ImageAssist artworkId={artworkId} kind="frame_detail" title="Generate a detail render with AI" defaultPresentation={fields.presentation_type || 'auto'} onApproved={() => bumpMedia('object')} />
          </div>
        </div>
      </StoryCard>

      {/* EDITION */}
      <StoryCard icon={Award} step="05" title="Edition & certification" hint="Edition numbering, signing and certificate."
        enabled={sections.edition.enabled} onToggle={() => setSec('edition', { enabled: !sections.edition.enabled })} testid="story-card-edition">
        {headBodyFields('edition', 'A limited edition with a lasting story', 'The story behind this edition and its authenticity.', 'edition')}
        <div className="grid grid-cols-2 gap-3">
          <div><FieldLabel>This edition number</FieldLabel><input type="number" value={fields.edition_number} onChange={(e) => setF('edition_number', e.target.value)} placeholder="3" className={inputCls} data-testid="story-edition-number" /></div>
          <div><FieldLabel>Edition of (total)</FieldLabel><input value={fields.edition_total === '' ? '' : fields.edition_total} disabled placeholder="Set in Print details above" className={`${inputCls} opacity-60`} data-testid="story-edition-total" /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <CheckRow label="Signed" checked={fields.signed} onClick={() => setF('signed', !fields.signed)} testid="story-signed" />
          <CheckRow label="Numbered" checked={fields.numbered} onClick={() => setF('numbered', !fields.numbered)} testid="story-numbered" />
          <CheckRow label="Certificate" checked={fields.certificate_included} onClick={() => setF('certificate_included', !fields.certificate_included)} testid="story-certificate" />
        </div>
        <div>
          <FieldLabel>Certificate image (optional)</FieldLabel>
          <AssetUpload value={fields.certificate_image_url} onChange={(v) => setF('certificate_image_url', v)} accept="image/*" testid="story-cert-image" />
        </div>
      </StoryCard>

      {/* IN CONTEXT */}
      <StoryCard icon={Images} step="06" title="In context" hint="The work shown at true scale in a space."
        enabled={sections.in_context.enabled} onToggle={() => setSec('in_context', { enabled: !sections.in_context.enabled })} testid="story-card-in_context">
        {headBodyFields('in_context', 'In a space of its own', 'Help the viewer imagine the work on their wall.', 'in_context')}
        <div>
          <FieldLabel>Context image(s) — 16:9 interior shots</FieldLabel>
          <SectionMediaManager artworkId={artworkId} section="in_context" label="Add a room / interior image, or generate one below." reloadSignal={mediaReload.in_context} />
          <div className="mt-3">
            <ImageAssist artworkId={artworkId} kind="in_context" title="Generate a room render with AI" defaultPresentation={fields.presentation_type || 'auto'} onApproved={() => bumpMedia('in_context')} />
          </div>
        </div>
      </StoryCard>

      {/* PROVENANCE */}
      <StoryCard icon={MapPin} step="07" title="Exhibited / provenance" hint="Shows automatically when records exist."
        hasToggle={false} testid="story-card-provenance">
        <div>
          <FieldLabel>Section heading (optional)</FieldLabel>
          <input value={sections.provenance.heading} onChange={(e) => setSec('provenance', { heading: e.target.value })}
            placeholder="Provenance" className={inputCls} data-testid="story-provenance-heading" />
          <p className="mt-1.5 text-[10px] font-mono text-[#555]">Heading is saved with “Save story”. Records below save individually.</p>
        </div>
        <ProvenanceEditor artworkId={artworkId} />
      </StoryCard>

      {/* ACQUISITION */}
      <StoryCard icon={ShoppingBag} step="08" title="Acquisition" hint="Always shown at the end — uses the existing inquiry flow."
        hasToggle={false} testid="story-card-acquisition">
        {headBodyFields('acquisition', 'Acquire this edition', 'An optional reassuring line above the inquiry button.', 'acquisition')}
      </StoryCard>
    </div>
  );
}
